import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { getCommunityOnboardingApi, postCommunityOnboardingMarkApi } from '@flipster/apis/v1/community';
import { overlayBunja } from '@flipster/overlay';
import { TextButton } from '@flipster/react/design-system';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { profileBunja } from '../../../../profile-page/bunja';
import ProfileSettingsModal from '../../../../profile-page/profile-settings-modal';
import { authBunja } from '../../../../shared/auth/state';
import { followBunja } from '../../../community/follow';
import { profileSettingBunja } from '../../../community/profile';

const followOnboardingModalCompletedAtom = atom(false);
export const followOnboardingBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { isLoggedInAtom } = bunja.use(authBunja);
  const overlay = bunja.use(overlayBunja);
  const { profileSettingsAtom } = bunja.use(profileBunja);
  const { postUserFollowMutationAtom, deleteUserFollowMutationAtom } = bunja.use(followBunja);
  const { putUserProfileMutationAtom } = bunja.use(profileSettingBunja);
  const communityOnboardingBaseAtom = atomWithQuery((get) => ({
    queryKey: ['getCommunityOnboardingApi'],
    queryFn: getCommunityOnboardingApi,
    enabled: get(isLoggedInAtom),
    refetchOnWindowFocus: false,
  }));
  const followSuggestionsAtom = atom((get) => get(communityOnboardingBaseAtom).data?.data.followSuggestions ?? []);
  const showFollowOnboardingAtom = atom(
    (get) =>
      !get(followOnboardingModalCompletedAtom) &&
      !(get(communityOnboardingBaseAtom).data?.data.onboardingViewed ?? true) &&
      get(followSuggestionsAtom).length > 0,
  );
  const shouldChangeToPublicProfileAtom = atom((get) => {
    const settings = get(profileSettingsAtom);
    return [settings?.performanceVisibility, settings?.tradeHistoryVisibility].some((v) => v === 'PRIVATE');
  });
  async function followUser(id: string) {
    const { mutateAsync } = store.get(postUserFollowMutationAtom);
    if (store.get(shouldChangeToPublicProfileAtom)) {
      const { mutateAsync: updateUserSettings } = store.get(putUserProfileMutationAtom);
      await updateUserSettings({ settings: { performanceVisibility: 'PUBLIC', tradeHistoryVisibility: 'PUBLIC' } });
      snackbar.success({
        message: 'Your trading activity and\nperformance are now visible',
        button: <TextButton label='See details' onClick={() => overlay.open(<ProfileSettingsModal />)} />,
      });
    }
    await mutateAsync(id);
  }
  async function unfollowUser(id: string) {
    const { mutateAsync } = store.get(deleteUserFollowMutationAtom);
    await mutateAsync(id);
  }
  async function completeOnboardingModal() {
    await postCommunityOnboardingMarkApi();
    store.set(followOnboardingModalCompletedAtom, true);
  }

  bunja.effect(() => {
    // why sub? see this: https://prextech.slack.com/archives/C07DN09JTUY/p1749042604293809
    return store.sub(shouldChangeToPublicProfileAtom, () => {});
  });

  return {
    completeOnboardingModal,
    followUser,
    unfollowUser,
    followSuggestionsAtom,
    showFollowOnboardingAtom,
  };
});
